import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import Markdown from 'react-markdown';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import { useNavigate } from 'react-router-dom';
import colors from '../../../../../../constants/colors';
import images from '../../../../../../constants/images';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import { axiosPath, path } from '../../../../../../routing/helpers/router';
import Translator from '../../../../../components/helpers/Translator';

export default function ChatBot() {
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const permissions = useSelector((state) => state.global.permissions);
  const route = useSelector((state) => state.global.route);
  const textAreaRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isLeft, setIsLeft] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadID, setThreadID] = useState();
  const [availableBots, setAvailableBots] = useState([]);
  const [conversationType, setConversationType] = useState(undefined);
  const [newFiltersHex, setNewFiltersHex] = useState();
  const [actualRoute, setActualRoute] = useState();
  const [aiObligationGroups, setAiObligationGroups] = useState([]);
  const [selectedAiObligationGroup, setSelectedAiObligationGroup] = useState();
  const [currentConvData, setCurrentConvData] = useState();

  const getAvailableBots = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_chatbot_getAvailableBots"), {
        moduleBridgeHex: route?.params?.moduleBridgeHex,
        plantHex: route?.params?.plantHex,
      })
      .then(({ data }) => {
        if (isMounted.current) {
          setAvailableBots(data.bots);
          setAiObligationGroups(data.aiObligationGroups);
          if (data.aiObligationGroups?.length === 1) {
            setSelectedAiObligationGroup(data.aiObligationGroups[0].id);
          }
        }
      })
      .catch((e) => {});
  }, [route]);

  useEffect(() => {
    if (actualRoute !== route.name) {
      getAvailableBots();
      setActualRoute(route.name);
    }
  }, [route]);

  useEffect(() => {
    if (newFiltersHex !== undefined) {
      navigate(
          path({
            name: 'user_module_legalCompliance_evaluation',
            params: {
              moduleBridgeHex: route?.params?.moduleBridgeHex,
              plantHex: route?.params?.plantHex,
              page: 1,
              filtersHex: newFiltersHex,
            },
          })
      );
    }
  }, [newFiltersHex]);

  const toggleChat = () => {
    setIsOpen((prev) => {
      const newState = !prev;
      if (newState) {
        setTimeout(() => {
          if (textAreaRef.current) {
            textAreaRef.current.focus();
          }
        }, 0); // Espera un ciclo de renderizado antes de enfocar
      }
      return newState;
    });
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  const toggleLeftRight = () => {
    setIsLeft(!isLeft);
  };

  const handleSubmit = useCallback(async () => {
    const msgs = [{ text: inputValue, isUser: true }, ...messages];
    setMessages(msgs);
    setInputValue('');
    setIsLoading(true);
    let convData;
    if (!threadID) {
      convData = {
        moduleBridgeHex: route?.params?.moduleBridgeHex,
        plantHex: route?.params?.plantHex,
      };
      await setCurrentConvData(convData);
    } else {
      convData = currentConvData;
    }
    axios
      .post(axiosPath("api_r_user_chatbot_chat"), {
        text: inputValue,
        moduleBridgeHex: convData?.moduleBridgeHex,
        plantHex: convData?.plantHex,
        threadID,
        conversationType,
        aiObligationGroupID: selectedAiObligationGroup,
      })
      .then(({ data }) => {
        if (isMounted.current) {
          setIsLoading(false);
          setMessages([{ text: data?.text, isUser: false }, ...msgs]);
          setThreadID(data?.threadID);
          if (data.changeFiltersHex) {
            setNewFiltersHex(data.changeFiltersHex);
          }
        }
      })
      .catch((e) => {
        setIsLoading(false);
        setMessages([
          { text: "Error", isUser: false, error: true },
          ...messages,
        ]);
      });
  }, [
    inputValue,
    messages,
    route,
    threadID,
    conversationType,
    currentConvData,
    selectedAiObligationGroup,
  ]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const resetChat = useCallback(() => {
    setInputValue('');
    setThreadID(undefined);
    setConversationType(undefined);
    setMessages([]);
    setCurrentConvData(undefined);
  }, [permissions]);

  if (availableBots?.length > 0) {
    return (
      <Container>
        {!isOpen && (
          <div className="chat-icon" onClick={toggleChat}>
            <img src={images.waibox} alt="waibox" />
          </div>
        )}

        {isOpen && (
          <ChatWindow
            className={isMaximized ? 'maximized' : ''}
            isLeft={isLeft}
          >
            <div className="chat-header">
              <div>
                <AppRemixIcon
                  icon={
                    !isMaximized ? 'expand-diagonal-2' : 'collapse-diagonal-2'
                  }
                  onClick={toggleMaximize}
                  size={18}
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="module.lc.chatbot.scale-window"
                    />,
                  )}
                  className="pointer mr-2"
                />
                <AppRemixIcon
                  icon="arrow-left-right"
                  onClick={toggleLeftRight}
                  size={18}
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="module.lc.chatbot.move-left-right"
                    />,
                  )}
                  className="pointer mr-2"
                />
              </div>

              <span>
                <AppRemixIcon
                  icon="chat-new"
                  onClick={resetChat}
                  size={18}
                  className="mr-3 pointer"
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="module.lc.chatbot.new-chat"
                    />,
                  )}
                />
                <AppRemixIcon
                  icon="close"
                  onClick={toggleChat}
                  size={18}
                  className="pointer"
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="module.lc.chatbot.close-chat"
                    />,
                  )}
                />
              </span>
            </div>
            {/* Mensajes */}
            <Messages>
              {messages.length === 0 && (
                <>
                  {!conversationType ? (
                    <>
                      <div>
                        {availableBots?.map((ab) => (
                          <div
                            key={ab}
                            className="btn btn-info btn-sm btn-block mb-2"
                            onClick={() => setConversationType(ab)}
                          >
                            {ab === 'aiObligationGroups' ? (
                              <>
                                {aiObligationGroups?.length > 1
                                  ? 'Asistente legal general'
                                  : aiObligationGroups?.[0].name}
                              </>
                            ) : (
                              <Translator
                                file="chatbot"
                                name={`chatbot.${ab}.title`}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                      <MessageWrapper isUser={false}>
                        <Avatar alt="Worldlex" src={images.waibox} />
                        <MessageBubble isUser={false}>
                          <Markdown>
                            {ReactDOMServer.renderToString(
                              <Translator
                                file="module-legalCompliance"
                                name="module.lc.chatbot.how-can-i-help"
                              />,
                            )}
                          </Markdown>
                        </MessageBubble>
                      </MessageWrapper>
                    </>
                  ) : (
                    <>
                      {conversationType === 'aiObligationGroups' &&
                        aiObligationGroups?.length > 1 &&
                        !selectedAiObligationGroup &&
                        aiObligationGroups?.map((aiog) => (
                          <div
                            key={aiog.id}
                            className="btn btn-info btn-sm btn-block mb-2"
                            onClick={() =>
                              setSelectedAiObligationGroup(aiog.id)
                            }
                          >
                            {aiog.name}
                          </div>
                        ))}
                    </>
                  )}
                  <AppNoInfo
                    text={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="module.lc.chatbot.non-anonymous-conversations"
                      />,
                    )}
                    icon={
                      <AppRemixIcon
                        icon="lock-unlock"
                        color={colors.grey500}
                        size={34}
                      />
                    }
                    minHeight={200}
                    color={colors.grey500}
                  />
                </>
              )}
              {isLoading && (
                <MessageWrapper isUser={false}>
                  <Avatar alt="Worldlex" src={images.waibox} />
                  <MessageBubble isUser={false}>
                    <Loader height="20" width="40">
                      {[10, 20, 30].map((c) => (
                        <circle
                          key={c}
                          className="dot"
                          cx={c}
                          cy="10"
                          r="2"
                          style={{ fill: colors.primary }}
                        />
                      ))}
                    </Loader>
                  </MessageBubble>
                </MessageWrapper>
              )}
              {messages.map((message, index) => (
                <MessageWrapper
                  key={index}
                  isUser={message.isUser}
                  error={message?.error}
                >
                  {!message.isUser && (
                    <Avatar alt="Worldlex" src={images.waibox} />
                  )}
                  <MessageBubble isUser={message.isUser}>
                    <Markdown>{message.text}</Markdown>
                    {/* <CopyButton
                  isUser={message.isUser}
                    onClick={() => copyToClibpoard(message.text)}
                  >
                    <AppRemixIcon icon="file-copy" />
                  </CopyButton> */}
                  </MessageBubble>
                  {message.isUser && (
                    <Avatar alt="Worldlex" src={auth.user?.client?.logoUrl} />
                  )}
                </MessageWrapper>
              ))}
            </Messages>
            <InputContainer>
              <TextArea
                ref={textAreaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={ReactDOMServer.renderToString(
                  <Translator
                    file="module-legalCompliance"
                    name="module.lc.chatbot.write-message"
                  />,
                )}
                rows="1"
                disabled={
                  !conversationType ||
                  (conversationType === 'aiObligationGroups' &&
                    !selectedAiObligationGroup)
                }
              />
              <Button
                onClick={handleSubmit}
                disabled={
                  !inputValue ||
                  !conversationType ||
                  (conversationType === 'aiObligationGroups' &&
                    !selectedAiObligationGroup)
                }
                className="btn btn-info"
              >
                <AppRemixIcon icon="send-plane-2" filled />
              </Button>
            </InputContainer>
            {/* Input para escribir mensajes */}
            {/* <div className="chat-input">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            />
            <button onClick={sendMessage}>Enviar</button>
          </div> */}
          </ChatWindow>
        )}
      </Container>
    );
  }
  return <></>;
}

const Container = styled.div`
  display: flex;
  gap: 10px;
  .chat-icon {
    position: fixed;
    bottom: 20px;
    right: 20px;
    height: 60px;
    width: 60px;
    background-color: white;
    border: 2px solid #496280;
    color: white;
    border-radius: 50%;
    cursor: pointer;
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
    img {
      padding: 4px;
      width: 60px;
      height: 60px;
      border-radius: 50%;
      border: 4px solid #496280;
    }
  }
`;

const ChatWindow = styled.div`
  position: fixed;
  bottom: 20px;
  ${({ isLeft }) => (isLeft ? 'left: 20px;' : 'right: 20px;')}
  width: 400px;
  height: 60vh;
  background-color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  resize: both;
  overflow: auto;
  &.maximized {
    width: 600px;
    height: 90vh;
  }
  .chat-header {
    display: flex;
    justify-content: space-between;
    padding: 10px;
    background-color: ${colors.primary};
    color: white;
    border-radius: 10px 10px 0 0;
  }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column-reverse;
`;

const MessageWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  margin-bottom: 30px;
  justify-content: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};

  &:first-child {
    margin-bottom: 16px;
  }
`;

const MessageBubble = styled.div`
  max-width: 60vw;
  padding: 10px;
  border-radius: 10px;
  position: relative;
  background-color: ${({ isUser, error }) =>
    error ? 'red' : isUser ? '#dcf8c6' : colors.grey300};
  h3 {
    font-size: 1.1em;
    font-weight: 700;
  }
  p {
    margin: 0;
  }
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
    bottom: 10px;
    ${({ isUser }) =>
      isUser
        ? `
      right: -10px;
      border-width: 10px 0 10px 10px;
      border-color: transparent transparent transparent #dcf8c6;
    `
        : `
      left: -10px;
      border-width: 10px 10px 10px 0;
      border-color: transparent ${colors.grey300} transparent transparent;
    `}
  }
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #ccc;
  margin: 0 10px;
`;

const Loader = styled.svg`
  @keyframes blink {
    50% {
      fill: transparent;
    }
  }
  .dot {
    animation: 1s blink infinite;
    fill: grey;
  }
  .dot:nth-child(2) {
    animation-delay: 250ms;
  }
  .dot:nth-child(3) {
    animation-delay: 500ms;
  }

  .loader {
    background-color: #f1f1f1;

    color: grey;
  }
`;

const InputContainer = styled.div`
  display: flex;
  padding: 10px;
  border-top: 1px solid #ccc;
  box-sizing: border-box;
`;

const TextArea = styled.textarea`
  flex: 1;
  padding: 10px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: none;
  overflow: hidden;
  line-height: 1.5;
`;

const Button = styled.button`
  padding: 10px 20px;
  margin-left: 10px;
  font-size: 16px;
  border: none;
  border-radius: 4px;

  &:hover {
    background-color: #0056b3;
  }
`;
